import endpoints from "../../../../utils/endpoints";
import { ENQUIRY_SOURCE } from "../../../../constants/enquirySource";
import type { FlowStep, OrderSnapshot, StepRuntime } from "../types";
import { FlowStepError } from "../engine/errors";
import { garaazTransitionPath, requireEnquiry, verifyEq, waitForEventWithGate } from "./common";

export const STEP_CREATE_ENQUIRY = "create_enquiry";
export const STEP_ACK_ENQUIRY = "ack_enquiry";
export const STEP_PART_CONVERSION_START = "part_conversion_start";
export const STEP_PART_CONVERSION_END = "part_conversion_end";

const isBakedEnquiry = (order: any) =>
  Array.isArray(order?.orderItems) && order.orderItems.length > 0;

/** Enquiry statuses once it is acknowledged (part conversion and estimate states sit after ACK). */
const ENQUIRY_ACKED_STATUSES = [
  "ACK",
  "PART_CONVERSION_STARTED",
  "PART_CONVERSION_COMPLETED",
  "DRAFT_ESTIMATE_SHARED",
  "DRAFT_ESTIMATE_RESPONDED",
  "FINAL_ESTIMATE_REQUESTED",
  "FINAL_ESTIMATE_SHARED",
];

/**
 * Same two-call rule as the OrCA footer (partConversionCommands): the
 * garaaz_transition is best effort (enquiries started via meta-info before the
 * states existed stay in ACK), the meta-info PATCH always runs because it fires
 * the Estimate Ready push and the IAM part confirmation.
 */
const tryPartConversionTransition = async (
  rt: StepRuntime,
  orderId: string,
  action: "START_PART_CONVERSION" | "COMPLETE_PART_CONVERSION"
) => {
  try {
    await rt.api({
      label: action === "START_PART_CONVERSION" ? "Start part conversion (state)" : "Complete part conversion (state)",
      method: "POST",
      path: garaazTransitionPath(orderId, action),
      body: {},
    });
  } catch (error: any) {
    rt.log(`${action} transition rejected, continuing with meta-info: ${error?.message || error}`, "warn");
  }
};

/**
 * Waits for the background ACK / part conversion the backend runs after a
 * baked create. On timeout the step can be done from here, like OrCA would.
 */
const waitForBakedEnquiryEvent = async (
  rt: StepRuntime,
  args: {
    expected: string;
    reached: (s?: OrderSnapshot) => boolean;
    describe: (s: OrderSnapshot) => string;
    fallbackLabel: string;
    fallbackDescription: string;
    runFallback: () => Promise<void>;
  }
) => {
  if (args.reached(rt.ctx.snapshot)) {
    rt.log(`Already done by the backend: ${args.expected}`, "info");
    rt.markEventProcessed();
    return;
  }
  await waitForEventWithGate(rt, {
    trigger: "Backend background processing of the baked enquiry",
    expected: args.expected,
    check: (s) => args.reached(s),
    describe: args.describe,
    allowContinue: false,
    fallback: {
      label: args.fallbackLabel,
      description: args.fallbackDescription,
      run: args.runFallback,
    },
  });
};

export const createEnquiryStep: FlowStep = {
  id: STEP_CREATE_ENQUIRY,
  name: "Place enquiry",
  targetLabel: "Enquiry placed",
  description:
    "Customer places an enquiry (POST /v1/orders without rootOrderId). The response comes back as NEW: for a baked enquiry (at least one part) the backend ACKs it and runs part conversion start+end in the background, which then sends part confirmation to the IAM supplier.",
  actor: "CUSTOMER",
  kind: "api",
  estimateMs: [1500, 4000],
  execute: async (rt) => {
    const { config, orcaUser } = rt.ctx;
    if (!config.customerId) throw new FlowStepError("No customer selected.");
    if (!config.parts.length) throw new FlowStepError("Add at least one part to the configuration.");
    if (!config.shipmentAddress) {
      throw new FlowStepError("No shipment address configured for the customer.");
    }

    const payload = {
      customerId: config.customerId,
      initiatorId: orcaUser._id,
      customerRemarks: config.remarks || undefined,
      shipmentAddress: config.shipmentAddress,
      billingDetails: config.billingDetails || undefined,
      orderDate: new Date().toISOString(),
      orderItems: config.parts.map((p) => ({
        partId: p.partId,
        orderedQuantity: p.quantity,
      })),
      orderMedia: config.orderMediaIds.length ? config.orderMediaIds : undefined,
      carMaker: config.carMaker || undefined,
      isTestOrder: config.isTestCustomer !== false,
      // Only sent when on, so the existing flow's payload is unchanged.
      ...(config.requirePriceEstimate ? { requirePriceEstimate: true } : {}),
      source: ENQUIRY_SOURCE.WEB_ORCA,
    };

    const res = await rt.api<any>({
      label: "Place enquiry",
      method: "POST",
      path: endpoints.placeGaraazOrder,
      body: payload,
      actor: "CUSTOMER",
    });
    const order = res.data?.order ?? res.data;
    if (!order?._id || !order?.orderNo) {
      throw new FlowStepError("Enquiry response did not include an order id/number.", {
        apiCall: res.record,
      });
    }
    if (config.isTestCustomer !== false && order.isTestOrder !== true) {
      throw new FlowStepError(
        "Safety stop: the created order is NOT flagged isTestOrder. Aborting so no production flow continues.",
        { expected: "isTestOrder = true", actual: `isTestOrder = ${order.isTestOrder}`, apiCall: res.record }
      );
    }
    rt.ctx.enquiry = { orderId: String(order._id), orderNo: order.orderNo };
    if (isBakedEnquiry(order) || rt.ctx.config.parts.length) {
      rt.log("Baked enquiry: backend ACKs and runs part conversion in the background", "info");
    }
    rt.log(
      `Enquiry ${order.orderNo} created (${order.orderStatus}) enquiryNo=${order.orderNo} _id=${order._id}`,
      "success"
    );
  },
  verify: (s) =>
    verifyEq(
      "enquiry.orderStatus",
      ["NEW", ...ENQUIRY_ACKED_STATUSES],
      s.enquiry?.orderStatus
    ),
};

export const ackEnquiryStep: FlowStep = {
  id: STEP_ACK_ENQUIRY,
  name: "Enquiry ACK (backend)",
  targetLabel: "Enquiry ACK",
  description:
    "Baked enquiry: the backend ACKs it in the background right after create. Waits for the enquiry to leave NEW; on timeout it can be ACK'd from here (POST /v1/orders/:id/garaaz_transition?action=ACK).",
  actor: "EVENT",
  kind: "event",
  estimateMs: [1000, 10000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    await waitForBakedEnquiryEvent(rt, {
      expected: "enquiry.orderStatus past NEW",
      reached: (s) => {
        const status = s?.enquiry?.orderStatus;
        return Boolean(status) && status !== "NEW";
      },
      describe: (s) => `enquiry=${s.enquiry?.orderStatus}`,
      fallbackLabel: "ACK from here",
      fallbackDescription: "POST /v1/orders/:id/garaaz_transition?action=ACK",
      runFallback: async () => {
        await rt.api({
          label: "ACK enquiry",
          method: "POST",
          path: garaazTransitionPath(enquiry.orderId, "ACK"),
          body: {},
        });
      },
    });
  },
  verify: (s) =>
    verifyEq("enquiry.orderStatus", [...ENQUIRY_ACKED_STATUSES, "ALLOCATED", "BLOCKED"], s.enquiry?.orderStatus),
};

export const partConversionStartStep: FlowStep = {
  id: STEP_PART_CONVERSION_START,
  name: "Part conversion started (backend)",
  targetLabel: "Part conversion started",
  description:
    "Baked enquiry: after the ACK the backend moves it to PART_CONVERSION_STARTED in the background. Waits for that; on timeout it can be started from here (garaaz_transition?action=START_PART_CONVERSION, then PATCH meta-info partConversionStartTime).",
  actor: "EVENT",
  kind: "event",
  estimateMs: [1000, 10000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    await waitForBakedEnquiryEvent(rt, {
      expected: "enquiry.partConversion = IN_PROGRESS | COMPLETED",
      reached: (s) => Boolean(s?.enquiry) && s!.enquiry!.partConversion !== "NOT_STARTED",
      describe: (s) => `enquiry=${s.enquiry?.orderStatus} partConversion=${s.enquiry?.partConversion}`,
      fallbackLabel: "Start part conversion from here",
      fallbackDescription:
        "garaaz_transition?action=START_PART_CONVERSION, then PATCH meta-info partConversionStartTime",
      runFallback: async () => {
        await tryPartConversionTransition(rt, enquiry.orderId, "START_PART_CONVERSION");
        await rt.api({
          label: "Start part conversion",
          method: "PATCH",
          path: endpoints.updateOrderMetaInfo(enquiry.orderId),
          body: { partConversionStartTime: new Date().toISOString() },
        });
      },
    });
  },
  verify: (s) =>
    verifyEq("enquiry.partConversion", ["IN_PROGRESS", "COMPLETED"], s.enquiry?.partConversion),
};

export const partConversionEndStep: FlowStep = {
  id: STEP_PART_CONVERSION_END,
  name: "Part conversion completed (backend)",
  targetLabel: "Part conversion completed",
  description:
    "Baked enquiry: the backend moves it to PART_CONVERSION_COMPLETED and stamps partConversionEndTime in the background. Waits for the stamp (it is what triggers the next step: without requirePriceEstimate the IAM part confirmation, with it the draft estimate). On timeout it can be completed from here (garaaz_transition?action=COMPLETE_PART_CONVERSION, then PATCH meta-info partConversionEndTime).",
  actor: "EVENT",
  kind: "event",
  estimateMs: [1000, 10000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    await waitForBakedEnquiryEvent(rt, {
      expected: "enquiry.partConversionEndTime stamped",
      reached: (s) => Boolean(s?.enquiry?.partConversionEndTime),
      describe: (s) =>
        `enquiry=${s.enquiry?.orderStatus} partConversionEndTime=${s.enquiry?.partConversionEndTime ?? "none"}`,
      fallbackLabel: "Complete part conversion from here",
      fallbackDescription:
        "garaaz_transition?action=COMPLETE_PART_CONVERSION, then PATCH meta-info partConversionEndTime",
      runFallback: async () => {
        await tryPartConversionTransition(rt, enquiry.orderId, "COMPLETE_PART_CONVERSION");
        await rt.api({
          label: "Complete part conversion",
          method: "PATCH",
          path: endpoints.updateOrderMetaInfo(enquiry.orderId),
          body: { partConversionEndTime: new Date().toISOString() },
        });
      },
    });
  },
  verify: (s) => verifyEq("enquiry.partConversion", "COMPLETED", s.enquiry?.partConversion),
};
